import * as React from 'react';
import { t, formatCzechDate, type MessageKey } from '@reos/i18n';
import { EmptyState } from '@redhand/ui';
import {
  approveAdvertisingAllocation,
  createAdvertisingInvoice,
  listAdvertisingInvoices,
  proposeAdvertisingAllocation,
  type AdvertisingInvoiceDto,
  type AdvertisingInvoiceStatus,
  type CreateAdvertisingInvoiceBody,
} from '../lib/advertising-api.js';
import { fetchRegistry, portalDisplayName } from '../lib/connectors.js';
import { fetchCaseAddressIndex } from '../lib/case-address-lookup.js';
import { DomainApiError } from '../lib/api-error.js';
import { DateField } from '../components/date-field.js';
import { CzechNumberInput } from '../components/czech-number-input.js';

/**
 * `/neprirazene-naklady` (krok O3) — inbox faktur od portalu. Kancelar fakturu
 * zaeviduje, necha si navrhnout rozpad podle publikaci v obdobi, castky pripadne
 * upravi a schvali; teprve schvalenim vzniknou radky `property_costs` (kategorie
 * inzerce), viditelne v `/naklady` i `/naklady-inzerce`.
 *
 * Soucet rozpadu musi sedet na fakturu — server to hlida (422), UI to jen rekne
 * driv a tlacitko „Schvalit" do te doby nepusti.
 */

const PORTAL_FALLBACK = 'sreality';

/** Strojovy duvod z advertising-service.ts (NO_PUBLICATIONS_REASON). */
const NO_PUBLICATIONS_REASON = 'no_publications_in_period';

/** Stav faktury je strojovy; popisek jde z katalogu (pravidlo 5). */
const STATUS_LABEL_KEYS: Readonly<Record<AdvertisingInvoiceStatus, MessageKey>> = {
  unallocated: 'webAdmin.neprirazeneNaklady.statusUnallocated',
  proposed: 'webAdmin.neprirazeneNaklady.statusProposed',
  approved: 'webAdmin.neprirazeneNaklady.statusApproved',
};

function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency }).format(value);
}

/**
 * Krok UXF3 bod 4 (#38): „Celkem (Kč)" = zaklad + DPH. Pocita se v halerich
 * pres BigInt jako vsechny ostatni castky na teto obrazovce (`toMinor`), ne
 * ve `number` — penize nikdy jako float (CLAUDE.md §3).
 * Prazdny nebo nesmyslny zaklad vrati `null`, aby UI ukazalo pomlcku misto 0 Kč.
 */
export function grossTotalMinor(amount: string, vatRate: string): bigint | null {
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(amount.trim())) return null;
  const base = toMinor(amount);
  const rate = vatRate.trim();
  if (rate === '') return base;
  if (!/^\d+(?:\.\d{1,2})?$/.test(rate)) return base;
  // sazba na bazicke body (21 -> 2100), aby slo delit celociselne
  const rateBp = toMinor(rate);
  const vat = (base * rateBp + 5000n) / 10000n;
  return base + vat;
}

function grossTotalLabel(amount: string, vatRate: string, currency: string): string {
  const total = grossTotalMinor(amount, vatRate);
  return total === null ? '—' : formatMoney(fromMinor(total), currency);
}

/** Soucet castek bez floatu: scitaji se halere (retezec -> celociselne minor). */
function sumMinor(amounts: readonly string[]): bigint {
  return amounts.reduce((sum, amount) => sum + toMinor(amount), 0n);
}

/** Zpet na numeric string — zase bez floatu, jen delenim BigInt. */
function fromMinor(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

function toMinor(amount: string): bigint {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  if (match === null) return 0n;
  const sign = match[1] === '-' ? -1n : 1n;
  const whole = BigInt(match[2] ?? '0');
  const fraction = BigInt((match[3] ?? '').padEnd(2, '0'));
  return sign * (whole * 100n + fraction);
}

interface InvoiceCardProps {
  readonly invoice: AdvertisingInvoiceDto;
  readonly portalLabel: (portal: string) => string;
  readonly propertyAddress: (propertyId: string) => string | undefined;
  readonly onChanged: (invoice: AdvertisingInvoiceDto) => Promise<void>;
  readonly onError: (message: string) => void;
}

function InvoiceCard({ invoice, portalLabel, propertyAddress, onChanged, onError }: InvoiceCardProps): React.JSX.Element {
  const [edits, setEdits] = React.useState<Readonly<Record<string, string>>>({});
  const [busy, setBusy] = React.useState<'propose' | 'approve' | null>(null);

  // Editace zije jen do prichodu nove verze rozpadu ze serveru. Zahozeni se dela
  // v renderu, ne v efektu: efekt bezi az po prvnim vykresleni, takze prepsal
  // castku, kterou clovek zmenil v tom okamziku driv, nez efekt stihl probehnout
  // (brana 2. 10. 2026 - spadlo pod zatizenim, viz REPORT-step-UXR1 "Oprava 1").
  // Prazdna mapa edits je rovnocenna puvodnimu predplneni: kazde cteni castky
  // (vcetne odeslani ke schvaleni) ma `?? allocation.amount` jako zaklad.
  const [editedVersion, setEditedVersion] = React.useState(invoice);
  if (editedVersion !== invoice) {
    setEditedVersion(invoice);
    setEdits({});
  }

  const editable = invoice.status !== 'approved';
  const amounts = invoice.allocations.map((a) => edits[a.propertyId] ?? a.amount);
  const sum = sumMinor(amounts);
  const matches = invoice.allocations.length > 0 && sum === toMinor(invoice.amount);

  async function run(kind: 'propose' | 'approve'): Promise<void> {
    setBusy(kind);
    try {
      const next =
        kind === 'propose'
          ? await proposeAdvertisingAllocation(invoice.id)
          : await approveAdvertisingAllocation(
              invoice.id,
              invoice.allocations.map((a) => ({ propertyId: a.propertyId, amount: edits[a.propertyId] ?? a.amount })),
            );
      await onChanged(next);
    } catch (e) {
      onError(e instanceof DomainApiError ? e.message : t('webAdmin.neprirazeneNaklady.loadError', { locale: 'cs' }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <article className="mb-4 rounded border border-gray-200 p-4" data-testid={`advertising-invoice-${invoice.id}`}>
      <header className="mb-2 flex flex-wrap items-baseline gap-3">
        <strong>{portalLabel(invoice.portal)}</strong>
        <span className="text-sm text-gray-600">
          {t('webAdmin.neprirazeneNaklady.periodLabel', {
            locale: 'cs',
            values: { from: formatCzechDate(invoice.periodFrom), to: formatCzechDate(invoice.periodTo) },
          })}
        </span>
        <span className="text-sm">{formatMoney(invoice.amount, invoice.currency)}</span>
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs" data-testid={`advertising-status-${invoice.id}`}>
          {t(STATUS_LABEL_KEYS[invoice.status], { locale: 'cs' })}
        </span>
      </header>

      {/*
        Krok UXF5 bod 8 (nalez A8/N9) — DRUHY RADEK POLOZKY JE JEJI VAZBA.
        Re-audit GPT (dukazni snimek E10): karta mluvila jen o portalu
        a castkach, takze z prehledu nebylo poznat, ceho se naklad tyka.
        Vazbu nese rozpad na nemovitosti (`allocations`) — kdyz zadny neni,
        rika se to VYSLOVNE, protoze prazdne misto si kazdy precte jako
        „nevim", ne jako „jeste neprirazeno".
      */}
      <p className="mb-2 text-sm text-gray-600" data-testid={`advertising-assignment-${invoice.id}`}>
        {invoice.allocations.length === 0
          ? t('webAdmin.neprirazeneNaklady.notAssigned', { locale: 'cs' })
          : `${t('webAdmin.neprirazeneNaklady.colProperty', { locale: 'cs' })}: ${invoice.allocations
              .map(
                (allocation) =>
                  propertyAddress(allocation.propertyId) ??
                  t('webAdmin.neprirazeneNaklady.propertyUnknown', { locale: 'cs' }),
              )
              .join(' · ')}`}
      </p>

      {invoice.statusReason === NO_PUBLICATIONS_REASON && (
        <p className="mb-2 text-sm text-amber-700" data-testid={`advertising-reason-${invoice.id}`}>
          {t('webAdmin.neprirazeneNaklady.reasonNoPublications', { locale: 'cs' })}
        </p>
      )}

      {invoice.allocations.length === 0 ? (
        <p className="mb-2 text-sm text-gray-600">{t('webAdmin.neprirazeneNaklady.noAllocations', { locale: 'cs' })}</p>
      ) : (
        <div className="mb-2 overflow-x-auto">
        <table className="text-sm" data-testid={`advertising-allocations-${invoice.id}`}>
          <thead>
            <tr className="text-left text-gray-500">
              <th style={{ minWidth: '4rem' }} className="whitespace-nowrap pr-6 font-normal">{t('webAdmin.neprirazeneNaklady.colProperty', { locale: 'cs' })}</th>
              <th style={{ minWidth: '4rem' }} className="whitespace-nowrap pr-6 font-normal">{t('webAdmin.neprirazeneNaklady.colDays', { locale: 'cs' })}</th>
              <th style={{ minWidth: '4rem' }} className="whitespace-nowrap pr-6 font-normal">{t('webAdmin.neprirazeneNaklady.colAmount', { locale: 'cs' })}</th>
              <th style={{ minWidth: '4rem' }} className="whitespace-nowrap font-normal">{t('webAdmin.neprirazeneNaklady.colMethod', { locale: 'cs' })}</th>
            </tr>
          </thead>
          <tbody>
            {invoice.allocations.map((allocation) => (
              <tr key={allocation.id}>
                {/*
                  Krok UXF3 bod 4 (N9): radek nese NAZEV nemovitosti, ne UUID.
                  Kdyz adresu neznáme, rika to slovy — zkraceny identifikator
                  („b455c79f…") makleri nic nerekne. Cele id zustava v `title`
                  pro podporu, uctarnu a hledani v auditu.
                */}
                <td className="pr-6" title={allocation.propertyId} data-testid={`advertising-property-${allocation.propertyId}`}>
                  {propertyAddress(allocation.propertyId) ?? t('webAdmin.neprirazeneNaklady.propertyUnknown', { locale: 'cs' })}
                </td>
                <td className="pr-6">{allocation.daysAdvertised ?? '—'}</td>
                <td className="pr-6">
                  {editable ? (
                    /*
                      Krok UXD2: pole bylo surovy text, takze „3 100,50" cetl
                      `toMinor` jako NULU — soucet rozpadu tise nesedel na
                      fakturu a schvaleni poslalo na server retezec, ktery
                      `Money` neprecte. Hodnota venku je proto kanonicka
                      (`3100.5`), cesky zapis zije v poli.
                    */
                    <CzechNumberInput
                      className="rh-input w-28"
                      ariaLabel={t('webAdmin.neprirazeneNaklady.colAmount', { locale: 'cs' })}
                      value={edits[allocation.propertyId] ?? allocation.amount}
                      onValueChange={(next) =>
                        setEdits((prev) => ({ ...prev, [allocation.propertyId]: next }))
                      }
                      testId={`advertising-amount-${allocation.propertyId}`}
                    />
                  ) : (
                    formatMoney(allocation.amount, allocation.currency)
                  )}
                </td>
                <td>
                  {t(
                    allocation.method === 'manual'
                      ? 'webAdmin.neprirazeneNaklady.methodManual'
                      : 'webAdmin.neprirazeneNaklady.methodAuto',
                    { locale: 'cs' },
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {invoice.allocations.length > 0 && (
        <p className="mb-2 text-sm" data-testid={`advertising-sum-${invoice.id}`}>
          {t('webAdmin.neprirazeneNaklady.sumLabel', { locale: 'cs' })}:{' '}
          {formatMoney(fromMinor(sum), invoice.currency)} ·{' '}
          {t('webAdmin.neprirazeneNaklady.invoiceTotalLabel', { locale: 'cs' })}:{' '}
          {formatMoney(invoice.amount, invoice.currency)}
          {editable && !matches ? (
            <span className="ml-2 text-red-600">{t('webAdmin.neprirazeneNaklady.sumMismatch', { locale: 'cs' })}</span>
          ) : null}
        </p>
      )}

      {invoice.status === 'approved' ? (
        <p className="text-sm text-gray-600">{t('webAdmin.neprirazeneNaklady.approvedNote', { locale: 'cs' })}</p>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            className="rh-btn"
            disabled={busy !== null}
            onClick={() => void run('propose')}
            data-testid={`advertising-propose-${invoice.id}`}
          >
            {busy === 'propose'
              ? t('webAdmin.neprirazeneNaklady.proposing', { locale: 'cs' })
              : t('webAdmin.neprirazeneNaklady.propose', { locale: 'cs' })}
          </button>
          <button
            type="button"
            className="rh-btn"
            disabled={busy !== null || !matches}
            onClick={() => void run('approve')}
            data-testid={`advertising-approve-${invoice.id}`}
          >
            {busy === 'approve'
              ? t('webAdmin.neprirazeneNaklady.approving', { locale: 'cs' })
              : t('webAdmin.neprirazeneNaklady.approve', { locale: 'cs' })}
          </button>
        </div>
      )}
    </article>
  );
}

export function NeprirazeneNakladyPage(): React.JSX.Element {
  const [invoices, setInvoices] = React.useState<AdvertisingInvoiceDto[] | null>(null);
  const [portals, setPortals] = React.useState<readonly { portal: string; name: string }[]>([]);
  const [addressByPropertyId, setAddressByPropertyId] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState({
    portal: PORTAL_FALLBACK,
    periodFrom: '',
    periodTo: '',
    amount: '',
    currency: 'CZK',
    vatRate: '',
    note: '',
  });

  /**
   * Krok APP2 bod 1 — ADRESY JEN PRO NEMOVITOSTI Z ROZPADU FAKTUR.
   *
   * Do APP2 se tu stahoval CELY seznam zakazek kancelare (`fetchCaseAddressIndex`
   * strankuje po stovkach) jen kvuli nazvu k `propertyId` radku rozpadu; pri
   * 343 zakazkach MV to byly ctyri dotazy navic a v DEMO kancelari kvota.
   * Id nemovitosti pritom obrazovka ZNÁ — jsou v rozpadu.
   *
   * JEDNA cesta k adresam pro obe mista, kde ta id vznikaji: nacteni stranky
   * a navrh/schvaleni rozpadu. Oba na ni CEKAJI pred `setInvoices` — efekt nad
   * hotovym stavem by adresu dotahl az PO prvnim vykresleni, takze karta by
   * blikla kodem nemovitosti (a cizi spec by cetl ten mezistav).
   * Adresa (krok O6a) je jen kosmetika: kdyz se nenacte, tabulka porad ukazuje
   * spravna cisla pod kodem nemovitosti.
   */
  const nactiAdresy = React.useCallback(async (ids: readonly string[]): Promise<void> => {
    const chybejici = [...new Set(ids)].filter((id) => id !== '');
    if (chybejici.length === 0) return;
    try {
      const index = await fetchCaseAddressIndex({ propertyIds: chybejici });
      setAddressByPropertyId((prev) => new Map([...prev, ...index.byPropertyId]));
    } catch {
      /* adresy nejsou kriticke */
    }
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listAdvertisingInvoices();
      // Adresy rozpadu PRED vykreslenim — jinak karta blikne kodem
      // nemovitosti a teprve pak ukaze adresu (viz `nactiAdresy`).
      await nactiAdresy(list.flatMap((invoice) => invoice.allocations.map((a) => a.propertyId)));
      setInvoices(list);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.neprirazeneNaklady.loadError', { locale: 'cs' }));
    } finally {
      setLoading(false);
    }
  }, [nactiAdresy]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const registry = await fetchRegistry();
        if (!cancelled) setPortals(registry.map((p) => ({ portal: p.portal, name: p.name })));
      } catch {
        /* popisky portalu nejsou kriticke */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const portalLabel = React.useCallback(
    (portal: string): string => portalDisplayName(portals.find((p) => p.portal === portal)?.name),
    [portals],
  );

  const propertyAddress = React.useCallback(
    (propertyId: string): string | undefined => addressByPropertyId.get(propertyId),
    [addressByPropertyId],
  );

  /**
   * Krok APP2 bod 1: adresy noveho rozpadu se dotahnou PRED tim, nez se radky
   * objevi na obrazovce. Jinak by tabulka blikla „Nemovitost bez adresy" a
   * teprve pak ukazala adresu — a rozpad navrzeny serverem je prvni moment,
   * kdy obrazovka id nemovitosti vubec zna.
   */
  async function replaceInvoice(next: AdvertisingInvoiceDto): Promise<void> {
    await nactiAdresy(next.allocations.map((a) => a.propertyId));
    setInvoices((prev) => (prev ?? []).map((invoice) => (invoice.id === next.id ? next : invoice)));
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const body = {
        portal: form.portal,
        periodFrom: form.periodFrom,
        periodTo: form.periodTo,
        amount: form.amount,
        currency: form.currency,
        ...(form.vatRate !== '' ? { vatRate: form.vatRate } : {}),
        ...(form.note !== '' ? { note: form.note } : {}),
      } as CreateAdvertisingInvoiceBody;
      const created = await createAdvertisingInvoice(body);
      setInvoices((prev) => [created, ...(prev ?? [])]);
      setForm((prev) => ({ ...prev, amount: '', vatRate: '', note: '' }));
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.neprirazeneNaklady.loadError', { locale: 'cs' }));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="p-6" data-testid="neprirazene-naklady-page">
      <h1 className="mb-1 text-xl font-bold">{t('webAdmin.neprirazeneNaklady.title', { locale: 'cs' })}</h1>
      <p className="mb-4 max-w-2xl text-sm text-gray-600">
        {t('webAdmin.neprirazeneNaklady.perex', { locale: 'cs' })}
      </p>

      <form className="mb-6 flex flex-wrap items-end gap-3" onSubmit={(e) => void submit(e)}>
        <label className="text-sm">
          {t('webAdmin.neprirazeneNaklady.portal', { locale: 'cs' })}
          <select
            className="rh-input block"
            value={form.portal}
            onChange={(e) => setForm((prev) => ({ ...prev, portal: e.target.value }))}
            data-testid="advertising-form-portal"
          >
            {(portals.length > 0 ? portals : [{ portal: PORTAL_FALLBACK, name: PORTAL_FALLBACK }]).map((p) => (
              <option key={p.portal} value={p.portal}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          {t('webAdmin.neprirazeneNaklady.periodFrom', { locale: 'cs' })}
          <DateField
            required
            className="rh-input block"
            value={form.periodFrom}
            onValueChange={(next) => setForm((prev) => ({ ...prev, periodFrom: next }))}
            testId="advertising-form-from"
          />
        </label>
        <label className="text-sm">
          {t('webAdmin.neprirazeneNaklady.periodTo', { locale: 'cs' })}
          <DateField
            required
            className="rh-input block"
            value={form.periodTo}
            onValueChange={(next) => setForm((prev) => ({ ...prev, periodTo: next }))}
            testId="advertising-form-to"
          />
        </label>
        {/*
          Krok UXF3 bod 4 (#38, N9): „Částka" neuvadela ani menu, ani zda je
          s DPH. Hodnota posilana API se NEMENI (`amount` je zaklad bez DPH,
          `vatRate` sazba) — meni se popisky a pricita se videt celkova
          castka, aby se makler nemusel domyslet, co zadava.
        */}
        <label className="text-sm">
          {t('webAdmin.neprirazeneNaklady.amountNet', { locale: 'cs' })}
          <CzechNumberInput
            required
            className="rh-input block w-32"
            value={form.amount}
            onValueChange={(next) => setForm((prev) => ({ ...prev, amount: next }))}
            testId="advertising-form-amount"
          />
        </label>
        <label className="text-sm">
          {t('webAdmin.neprirazeneNaklady.vat', { locale: 'cs' })}
          {/* Sazba je taky cislo s desetinami („21,5") — tedy tentyz vstup. */}
          <CzechNumberInput
            className="rh-input block w-24"
            value={form.vatRate}
            onValueChange={(next) => setForm((prev) => ({ ...prev, vatRate: next }))}
            testId="advertising-form-vat"
          />
        </label>
        <p className="text-sm">
          <span className="block text-gray-600">{t('webAdmin.neprirazeneNaklady.amountGross', { locale: 'cs' })}</span>
          <strong data-testid="advertising-form-total">{grossTotalLabel(form.amount, form.vatRate, form.currency)}</strong>
        </p>
        <label className="text-sm">
          {t('webAdmin.neprirazeneNaklady.note', { locale: 'cs' })}
          <input
            className="rh-input block"
            value={form.note}
            onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
            data-testid="advertising-form-note"
          />
        </label>
        <button type="submit" className="rh-btn" disabled={creating} data-testid="advertising-form-submit">
          {creating
            ? t('webAdmin.neprirazeneNaklady.creating', { locale: 'cs' })
            : t('webAdmin.neprirazeneNaklady.create', { locale: 'cs' })}
        </button>
      </form>

      {loading && <p role="status">{t('webAdmin.neprirazeneNaklady.loading', { locale: 'cs' })}</p>}
      {error !== null && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}

      {!loading && (invoices?.length ?? 0) === 0 ? (
        <EmptyState
          title={t('webAdmin.neprirazeneNaklady.emptyTitle', { locale: 'cs' })}
          description={t('webAdmin.neprirazeneNaklady.emptyDescription', { locale: 'cs' })}
        />
      ) : (
        (invoices ?? []).map((invoice) => (
          <InvoiceCard
            key={invoice.id}
            invoice={invoice}
            portalLabel={portalLabel}
            propertyAddress={propertyAddress}
            onChanged={replaceInvoice}
            onError={setError}
          />
        ))
      )}
    </div>
  );
}
